import { ReactNode, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
/* Icons start */
import AddCircle from '@mui/icons-material/AddCircle';
/* Icons end */
import {
  Autocomplete,
  AutocompleteProps,
  FilterOptionsState,
  createFilterOptions,
  CircularProgress,
  styled,
  Typography,
} from '@mui/material';

import { TextField } from './textField.component';
import { ListboxComponent, StyledPopper } from '../../virtualization/virtualization.component';

const SelectBase = forwardRef<HTMLDivElement, ISelectBaseProps>(
  (
    {
      shrink,
      label,
      width,
      disableTextInput,
      placeholder,
      popperWidth,
      required,
      error,
      helperText,
      options,
      ...props
    },
    ref,
  ) => {
    options.forEach((el) => {
      el.label = el.label || '';
    });
    const { t } = useTranslation();
    const { autoFocus, fontSize } = props;

    return (
      <Autocomplete
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => {
          const { InputProps, inputProps, InputLabelProps, ...otherParams } = params;
          return (
            <TextField
              {...otherParams}
              size='small'
              label={label}
              {...(props.name && {
                name: props.name,
              })}
              error={error}
              helperText={helperText}
              slotProps={{
                inputLabel: { ...InputLabelProps, shrink: true },
                formHelperText: {
                  sx: { marginLeft: 0 },
                },
                input: {
                  ...InputProps,
                  endAdornment: (
                    <>
                      {props.loading && <CircularProgress size={20} />}
                      {InputProps.endAdornment}
                    </>
                  ),
                },
                htmlInput: {
                  ...inputProps,
                  ...(disableTextInput && { readOnly: true }),
                },
              }}
              {...(!props.multiple && {
                sx: {
                  '& .MuiButtonBase-root.MuiAutocomplete-clearIndicator': {
                    visibility: 'visible',
                  },
                },
              })}
              {...{ autoFocus, required, ...(placeholder && { placeholder }) }}
            />
          );
        }}
        fullWidth
        sx={{
          width: '100%',
          maxWidth: (theme) =>
            width || (shrink ? theme.form.fieldShrinkWidth : theme.form.fieldWidth),
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              width: popperWidth ?? '100%',
              minWidth: '100%',
            },
          },
        }}
        clearText={t('common.clear')}
        noOptionsText={t('common.no_data')}
        renderOption={(props, option) => (
          <SList
            title={option.label}
            style={{ ...(fontSize && { fontSize }) }}
            {...props}
            key={option.id}
          >
            <span>{option.label}</span>
          </SList>
        )}
        ref={ref}
        options={options}
        {...props}
      />
    );
  },
);

export const Select = forwardRef<HTMLDivElement, ISelectProps>((props, ref) => {
  const { fontSize, datasetattribute } = props;
  return (
    <SelectBase
      {...props}
      renderOption={(props, option) =>
        [
          {
            ...props,
            style: {
              ...(fontSize && { fontSize }),
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              display: 'inline-block',
            },
            title: option.label,
          },
          option,
          datasetattribute,
        ] as ReactNode
      }
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      onChange={(e, v, r) => props.onChange?.(e, v as ISelectOption | null, r)}
      ref={ref}
    />
  );
});

export const SelectWithImages = forwardRef<HTMLDivElement, ISelectWithImagesProps>((props, ref) => (
  <SelectBase
    {...props}
    ref={ref}
    onChange={(e, v, r) => props.onChange?.(e, v as IOptionWithImage, r)}
    renderOption={(props, v) => {
      const option = v as IOptionWithImage;
      return (
        <SList title={option.label} {...props} key={option.label}>
          {option?.img}
          <Typography
            sx={{
              ml: '10px',
            }}
          >
            {option.label}
          </Typography>
        </SList>
      );
    }}
  />
));

export const SelectMultiple = forwardRef<HTMLDivElement, ISelectMultipleProps>((props, ref) => (
  <SelectBase
    {...props}
    onChange={(e, v, r) => props.onChange?.(e, v as ISelectOption[], r)}
    multiple
    ref={ref}
  />
));
export const SelectFreeSolo = forwardRef<HTMLDivElement, ISelectFreeSoloProps>((props, ref) => {
  const { t } = useTranslation();
  const filter = createFilterOptions<IFreeSoloOption>();

  const onFilterOptions = (
    options: ISelectOption[],
    state: FilterOptionsState<ISelectOption>,
  ): ISelectOption[] => {
    const filtered = filter(options, state);
    const { inputValue } = state;
    const isExisting = options.some((option) => inputValue === option.label);
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        inputValue,
        label: `${t('common.add')}: "${inputValue}"`,
        id: inputValue,
      });
    }
    return filtered;
  };

  return (
    <SelectBase
      {...props}
      onChange={(e, v, r) => props.onChange?.(e, v as ISelectOption | null, r)}
      getOptionLabel={(option: string | IFreeSoloOption): string =>
        typeof option === 'string' ? option : option.inputValue || option.label
      }
      clearOnBlur
      filterOptions={onFilterOptions}
      renderOption={(props, option) => (
        <SList
          title={option.label}
          {...props}
          key={Object.getOwnPropertyDescriptor(props, 'data-option-index')?.value}
        >
          {(option as IFreeSoloSelectOption).inputValue && (
            <AddCircle sx={(theme) => ({ mr: 1, fill: theme.palette.grey['600'] })} />
          )}
          <span>{option.label}</span>
        </SList>
      )}
      selectOnFocus
      freeSolo
      ref={ref}
    />
  );
});

type TSelectSingle = AutocompleteProps<ISelectOption, false, false, false>;
type TSelectMultiple = AutocompleteProps<ISelectOption, true, false, false>;
type TSelectFreeSolo = AutocompleteProps<ISelectOption, false, true | false, false>;

export interface ISelectOption {
  id: string | number;
  label: string;
  disabled?: boolean;
}
interface ISelectBaseProps
  extends Omit<
    AutocompleteProps<ISelectOption, true | false, true | false, true | false>,
    'renderInput'
  > {
  label?: string;
  shrink?: boolean;
  width?: string;
  popperWidth?: string;
  disableTextInput?: boolean;
  placeholder?: string;
  required?: boolean;
  fontSize?: string;
  error?: boolean;
  helperText?: string;
  datasetattribute?: string;
  name?: string;
}
export interface IFreeSoloOption extends ISelectOption {
  inputValue?: string;
}
export interface ISelectProps extends Omit<ISelectBaseProps, 'value' | 'onChange' | 'multiple'> {
  value?: TSelectSingle['value'];
  onChange?: TSelectSingle['onChange'];
}
export interface ISelectMultipleProps
  extends Omit<ISelectBaseProps, 'value' | 'onChange' | 'multiple'> {
  value?: TSelectMultiple['value'];
  onChange?: TSelectMultiple['onChange'];
}
export interface ISelectWithImagesProps
  extends Omit<ISelectBaseProps, 'value' | 'onChange' | 'multiple'> {
  value?: TSelectSingle['value'];
  onChange?: TSelectSingle['onChange'];
  option?: IOptionWithImage[];
}

interface IOptionWithImage {
  id: string;
  label: string;
  img: ReactNode;
}
export interface ISelectFreeSoloProps
  extends Omit<ISelectBaseProps, 'value' | 'onChange' | 'multiple'> {
  value?: TSelectFreeSolo['value'];
  onChange?: TSelectFreeSolo['onChange'];
}
interface IFreeSoloSelectOption extends ISelectOption {
  inputValue?: string;
}

const SList = styled('li')({
  '& > span': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
});
